import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { log } from '../../utils/jsUtils';
import { useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import memberDrawerStyles from '../Styles/MemberDrawer.Style';

import {
  selectSubscriptionInfo,
  selectSubscriptionStatus,
  selectPlanInfo,
  selectSubscriptionId,
  selectBillingState,
  selectPlanId
} from '../../reduxStore/modules/billing';
import SPBackend from '../../services/SPBackend';
import { selectAccountUsage, selectUser } from '../../reduxStore/modules/user';
import {
  getBillingStateMessage,
  getBillingActionType,
  getButtonText,
  isValidSubscriptionStatus,
  hasNoSubscription
} from '../../utils/billingValidationUtils';

const SubscriptionStatus = React.memo(({ onRecharge, onUpgrade, onBilling, isLoading = false }) => {
  const [billingDataReady, setBillingDataReady] = useState(false);
  const [availablePlanPriceIds, setAvailablePlanPriceIds] = useState(new Set());
  // Track if initial billing data load has completed to prevent flashing during subsequent refreshes
  const hasCompletedInitialBillingLoad = useRef(false);
  const history = useHistory();

  // Handle billing navigation - use provided prop or navigate to billing screen
  const handleBillingClick = () => {
    if (onBilling) {
      onBilling();
    } else {
      history.push('/billing');
    }
  };

  // Get data directly from Redux
  const subscriptionInfo = useSelector(selectSubscriptionInfo);
  const planInfo = useSelector(selectPlanInfo);
  const accountUsage = useSelector(selectAccountUsage);
  const subscriptionStatus = useSelector(selectSubscriptionStatus);
  const subscriptionId = useSelector(selectSubscriptionId);
  const user = useSelector(selectUser);
  const billingState = useSelector(selectBillingState);
  const billingPlanId = useSelector(selectPlanId);

  // Fetch available plan price IDs once on mount to detect new vs legacy plans
  useEffect(() => {
    if (!user) return;
    SPBackend.getPlans(user)
      .then((response) => {
        const plans = response?.data?.plans || [];
        const priceIds = new Set();
        plans.forEach((plan) => {
          if (plan.prices?.monthly?.id) priceIds.add(plan.prices.monthly.id);
          if (plan.prices?.yearly?.id) priceIds.add(plan.prices.yearly.id);
        });
        setAvailablePlanPriceIds(priceIds);
      })
      .catch((err) => {
        log('[SubscriptionStatus] Failed to fetch plans for new-plan detection:', err);
      });
  }, [user?.uid]);

  // New-plan detection and credits visibility logic
  const isNewPlan = billingPlanId && availablePlanPriceIds.size > 0 && availablePlanPriceIds.has(billingPlanId);
  const hasTopups = accountUsage?.Topups?.length > 0;
  const creditsBalance = Number(accountUsage?.TotalCreditsBalance) || 0;
  const shouldHideCreditsUI = isNewPlan && !hasTopups && creditsBalance > 0;
  const shouldShowLimitReached = isNewPlan && !hasTopups && creditsBalance === 0;

  // Determine if we have sufficient billing data to show correct buttons
  const hasSufficientBillingData = () => {
    // Only hide buttons if truly no data (loading state)
    if (!subscriptionStatus || subscriptionStatus === 'na') {
      return false;
    }

    // If we have a valid subscription status and user data, we can show appropriate UI
    if (isValidSubscriptionStatus(subscriptionStatus) && user) {
      return true;
    }

    // Handle legacy active/trialing requirements for backward compatibility
    if (subscriptionStatus === 'active') {
      return !!subscriptionId;
    }

    if (subscriptionStatus === 'trialing') {
      return subscriptionStatus && user;
    }

    // For any other status, if we have status and user, we can show UI
    return subscriptionStatus && subscriptionStatus !== 'na' && user;
  };

  // Monitor billing data readiness with timing-based safety
  useEffect(() => {
    const isDataSufficient = hasSufficientBillingData();

    // Development-only debug log
    if (process.env.NODE_ENV === 'development') {
      log('[SubscriptionStatus] Data check:', {
        subscriptionStatus,
        hasSubscriptionId: !!subscriptionId,
        isDataSufficient,
        billingDataReady
      });
    }

    if (isDataSufficient && !billingDataReady) {
      // Add a small delay to ensure billing validation completes
      const timer = setTimeout(() => {
        setBillingDataReady(true);
        hasCompletedInitialBillingLoad.current = true; // Mark initial load complete
      }, 300); // 300ms delay for billing validation to complete

      return () => clearTimeout(timer);
    }
    // REMOVED: else-if that reset billingDataReady to false
    // Once billing data is ready, keep it ready to prevent flashing during background refreshes
  }, [subscriptionStatus, subscriptionId, planInfo, subscriptionInfo, user]);

  // log('[SubscriptionStatus] billing: ', billing);
  // log('[SubscriptionStatus] subscriptionInfo: ', subscriptionInfo);
  // log('[SubscriptionStatus] planInfo: ', planInfo);
  // log('[SubscriptionStatus] accountUsage: ', accountUsage);
  // log('[SubscriptionStatus] subscriptionStatus: ', subscriptionStatus);
  // log('[SubscriptionStatus] subscriptionId: ', subscriptionId);


  // If we don't have enough data to display properly, don't render
  if (!subscriptionStatus) {
    log('[SubscriptionStatus] No subscription status, not rendering');
    return <></>;
  }

  // Keep default signup plan ID for potential future use or other logic
  const defaultSignupPlanId = process.env.REACT_APP_ACCOUNT_DEFAULT_SIGNUP_PLAN;

  // Environment variable validation
  if (!defaultSignupPlanId) {
    console.warn('[SubscriptionStatus] REACT_APP_ACCOUNT_DEFAULT_SIGNUP_PLAN not set - trial plan detection may not work correctly');
  }

  // Show content after initial load completes - don't hide during background refreshes
  const shouldShowContent = hasCompletedInitialBillingLoad.current || (!isLoading && billingDataReady);

  // Ensure we show at least a basic version even if some data is missing
  return (
    <ListItem
      sx={(theme) => ({
        ...memberDrawerStyles.listItem(theme),
        ...memberDrawerStyles.upgradeItem(theme)
      })}
    >
      {/*
        Button Logic with Enhanced Validation:
        - Hide buttons until we have sufficient billing data (billingDataReady=true)
        - Also respect the general isLoading prop from parent
        - Use utility function to determine correct action (recharge vs upgrade)
        - Show appropriate messaging based on billing state

        This prevents incorrect buttons and provides better user guidance.
      */}
      {shouldShowContent && (() => {
        // Use utility function to determine the correct action
        const actionType = getBillingActionType(billingState);
        const buttonText = getButtonText(subscriptionStatus);

        // Get appropriate tooltip based on subscription status
        const getButtonTooltip = () => {
          switch (subscriptionStatus) {
            case 'canceled':
              return 'Your subscription ended. Click to subscribe again';
            case 'incomplete':
              return 'Complete your payment setup to activate subscription';
            case 'incomplete_expired':
              return 'Payment setup expired. Start fresh with a new subscription';
            case 'past_due':
              return 'Update your payment method to continue service';
            case 'unpaid':
              return 'Update payment method to restore access';
            case 'paused':
              return 'Resume your subscription to continue using the service';
            default:
              return actionType === 'recharge'
                ? 'Add more credits to your account'
                : actionType === 'billing'
                ? 'Manage your billing and payment methods'
                : 'Subscribe to access premium features';
          }
        };

        // Trialing users should see "Activate" instead of "Upgrade"
        const isTrialing = subscriptionStatus === 'trialing';

        // Render button based on action type
        if (actionType === 'recharge') {
          // Hide Recharge button for new-plan users with no topups and credits remaining
          if (shouldHideCreditsUI) return null;
          return <Button
            sx={(theme) => memberDrawerStyles.rechargeBtn(theme)}
            color='primary'
            onClick={onRecharge}
            variant='contained'
            title={getButtonTooltip()}
          >{buttonText}</Button>;
        } else if (actionType === 'billing') {
          return <Button
            sx={(theme) => memberDrawerStyles.upgradeBtn(theme)}
            color='warning'
            onClick={handleBillingClick}
            variant='contained'
            title={getButtonTooltip()}
          >{buttonText}</Button>;
        } else if (isTrialing) {
          return <Button
            sx={(theme) => memberDrawerStyles.upgradeBtn(theme)}
            color='secondary'
            onClick={() => history.push('/activate')}
            variant='contained'
            title='Activate your account and start your subscription'
          >{'Activate'}</Button>;
        } else {
          return <Button
            sx={(theme) => memberDrawerStyles.upgradeBtn(theme)}
            color='secondary'
            onClick={onUpgrade}
            variant='contained'
            title={getButtonTooltip()}
          >{buttonText}</Button>;
        }
      })()}

      {/* Trial info - hide until billing data is ready to avoid showing stale data */}
      {shouldShowContent && (
        subscriptionStatus === 'trialing' && subscriptionInfo && subscriptionInfo.current_period_end
        ? moment.unix(subscriptionInfo.current_period_end).diff(moment(), 'days') > 0
          ? <Typography variant='body1'>{`${moment.unix(subscriptionInfo.current_period_end).diff(moment(), 'days') + 1} days trial remaining`}</Typography>
          : <Typography variant='body1'>{'Last trial day'}</Typography>
        : subscriptionStatus === 'trialing'
          ? <Typography variant='body1'>{'Trial active'}</Typography>
          : null
      )}

      {/* Essential status info and 2-word explanations below buttons */}
      {shouldShowContent && (() => {
        const actionType = getBillingActionType(billingState);

        // For active subscriptions (Recharge button), show credit balance
        if (actionType === 'recharge' && subscriptionStatus === 'active') {
          // Hide credits entirely for new-plan users with no topups and credits remaining
          if (shouldHideCreditsUI) return null;
          // Show "0 credits" + "Limit reached" for new-plan users who exhausted credits
          if (shouldShowLimitReached) {
            return <>
              <Typography variant='body1'>0 credits</Typography>
              <Typography variant='caption'>Limit reached</Typography>
            </>;
          }
          return <Typography variant='body1'>{
            !accountUsage || !accountUsage.CurrentPeriod
              ? '0 credits'
              : Number.isNaN(Number(accountUsage.TotalCreditsBalance)) ? '0' : `${Number(accountUsage.TotalCreditsBalance).toLocaleString()} credits`
          }</Typography>;
        }

        // For payment issues (Billing button), show payment status
        if (actionType === 'billing') {
          const billingExplanations = {
            'past_due': 'payment failed',
            'unpaid': 'payment required'
          };
          return <Typography variant='body1'>{billingExplanations[subscriptionStatus]}</Typography>;
        }

        // For expired trials (special legacy case) - check BEFORE general upgrade explanations
        if ((subscriptionStatus !== 'trialing' && subscriptionStatus !== 'active')
          && planInfo && planInfo.id === defaultSignupPlanId) {
          return <Typography variant='body1'>{'trial expired'}</Typography>;
        }

        // For subscription management (Upgrade button), show subscription status
        // Note: 'trialing' is handled by the trial info section above (lines 199-208)
        if (actionType === 'upgrade') {
          const upgradeExplanations = {
            'canceled': 'Subscription ended',
            'incomplete': 'Setup incomplete',
            'incomplete_expired': 'Setup expired',
            'paused': 'Subscription paused',
            'na': 'No subscription'
          };
          return <Typography variant='body1'>{upgradeExplanations[subscriptionStatus]}</Typography>;
        }

        // Fallback for unmatched cases
        return null;
      })()}
    </ListItem>
  );
});

SubscriptionStatus.displayName = 'SubscriptionStatus';

export default SubscriptionStatus;